import { useState } from "react";
import { Home, Mail, Lock, LogIn, UserPlus } from "lucide-react";
import InfoBentoCard from "@ui/InfoBentoCard";
import { useAuth } from "@contexts/AuthContext";

export default function Login() {
  const { signInEmail, signUpEmail, signInGoogle } = useAuth();
  const [mode, setMode] = useState("signin"); // 'signin' | 'signup'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "signin") {
        await signInEmail(email, password);
      } else {
        await signUpEmail(email, password);
      }
    } catch (err) {
      setError(err.message.replace("Firebase: ", ""));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setBusy(true);
    try {
      await signInGoogle();
    } catch (err) {
      setError(err.message.replace("Firebase: ", ""));
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    background: "var(--bg-input)",
    border: "1px solid var(--border-input)",
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "var(--bg-root)" }}
    >
      <div className="w-full max-w-md">
        <InfoBentoCard
          cornerGlow
          frostedGradient
          icon={<Home className="w-4 h-4" />}
          accent="amber"
          title="Posh Block"
          subtitle="Renovation, managed."
        >
          <form onSubmit={handleSubmit} className="space-y-3 mt-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full text-xs text-gray-200 rounded-lg pl-8 pr-2 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  style={inputStyle}
                  placeholder="you@example.com"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">
                Password
              </label>
              <div className="relative">
                <Lock className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full text-xs text-gray-200 rounded-lg pl-8 pr-2 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  style={inputStyle}
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <p className="text-[11px] text-red-400">{error}</p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors disabled:opacity-50"
            >
              {mode === "signin" ? <LogIn className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
              {mode === "signin" ? "Sign In" : "Create Account"}
            </button>

            <div className="flex items-center gap-2 my-2">
              <div className="flex-1 h-px" style={{ background: "var(--border-row)" }} />
              <span className="text-[10px] text-gray-600">or</span>
              <div className="flex-1 h-px" style={{ background: "var(--border-row)" }} />
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg transition-colors text-gray-300 hover:text-white disabled:opacity-50"
              style={inputStyle}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <button
              type="button"
              onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
              className="w-full text-[11px] text-gray-500 hover:text-gray-300 transition-colors mt-2"
            >
              {mode === "signin" ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </form>
        </InfoBentoCard>
      </div>
    </div>
  );
}
